import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Award, Heart, Users, Calendar, ArrowRight,
  CheckCircle, Clock, Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BRAND, IMAGES } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Leaf className="w-4 h-4 text-green-300" />
                <span className="text-sm font-medium">About Us</span>
              </span>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                Meet {BRAND.doctorName}
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8">
                A passionate homoeopath with over 20 years of experience in treating 
                chronic and acute conditions through classical homoeopathy.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <span>⚕️ BHMS</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <span>🎖️ F.C.A.H</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <span>20+ Years Experience</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-white/10 rounded-3xl"></div>
              <img
                src={IMAGES.doctor}
                alt={BRAND.doctorName}
                className="relative rounded-3xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#2F5233] font-medium">Our Philosophy</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Treating the Whole Person
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At JB's Homoeopathy, we believe that true healing comes from understanding 
                the individual as a whole - mind, body, and spirit. Our approach goes beyond 
                treating symptoms to address the root cause of illness.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Dr. Jenice Bhatia takes the time to understand each patient's unique 
                constitution, lifestyle, emotional state, and medical history to prescribe 
                the most suitable homoeopathic remedy.
              </p>
              
              <div className="space-y-4">
                {[
                  'Individualized treatment plans',
                  'Gentle, non-toxic medicines',
                  'Focus on long-term wellness',
                  'Comprehensive case analysis'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2F5233]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-[#2F5233]/5 border-0">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#2F5233]/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-[#2F5233]" />
                  </div>
                  <p className="text-3xl font-bold text-[#2F5233]">10000+</p>
                  <p className="text-gray-600 text-sm">Patients</p>
                </CardContent>
              </Card>
              
              <Card className="bg-[#2F5233]/5 border-0 mt-8">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#2F5233]/10 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-[#2F5233]" />
                  </div>
                  <p className="text-3xl font-bold text-[#2F5233]">20+</p>
                  <p className="text-gray-600 text-sm">Years Experience</p>
                </CardContent>
              </Card>
              
              <Card className="bg-[#2F5233]/5 border-0 mt-8">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#2F5233]/10 flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-7 h-7 text-[#2F5233]" />
                  </div>
                  <p className="text-3xl font-bold text-[#2F5233]">100%</p>
                  <p className="text-gray-600 text-sm">Natural Care</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2F5233] font-medium">Qualifications</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              Education & Expertise
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: GraduationCap,
                title: '⚕️ BHMS',
                subtitle: 'Bachelor of Homoeopathic Medicine & Surgery',
                description: 'Completed from a premier homoeopathic medical college with distinction.'
              },
              {
                icon: Award,
                title: 'F.C.A.H',
                subtitle: 'Fellowship in Classical & Advanced Homoeopathy',
                description: 'Advanced fellowship-focused training in constitutional prescribing and chronic case management.'
              }
            ].map((qual, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-[#2F5233]/10 flex items-center justify-center mb-6">
                    <qual.icon className="w-8 h-8 text-[#2F5233]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{qual.title}</h3>
                  <p className="text-[#2F5233] font-medium text-sm mb-4">{qual.subtitle}</p>
                  <p className="text-gray-600">{qual.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience Holistic Healing?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Book your consultation today and take the first step towards natural wellness 
            with {BRAND.doctorName}.
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-white text-[#2F5233] hover:bg-white/90">
              <Calendar className="w-5 h-5 mr-2" />
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
